import type { ReactNode } from "react";
import { Page, Tabs } from "@cntrl/ui";
import { useCapability } from "../../app/bandContext.js";
import { useBandTutorials } from "../../app/hooks/useTutorials.js";
import { visibleSettingsTabs, type SettingsTab } from "./settingsTabs.js";

export type { SettingsTab };
import styles from "./SettingsScreen.module.scss";
import { usePhoneLayout } from "../../app/phoneLayout.js";

export interface SettingsScreenProps {
  /** The active tab (derived from the route by the router wrapper). */
  activeTab: SettingsTab;
  /** Navigate to a tab's route (`/settings/account` | `/settings/connections` | …). */
  onTab: (tab: SettingsTab) => void;
  /** The active tab body — the routed `<Outlet/>` (SettingsAccountTab | SettingsConnectionsTab | …). */
  children: ReactNode;
}

/**
 * Settings shell for the ROUTED page. The modal (`SettingsModal`, opened from the topbar gear) is the normal
 * way in; this survives because the OAuth connect flow returns to `/settings/connections` as a cold page load
 * and the mobile shell has its own Settings tab. Both mount the same tab bodies.
 *
 * (the `/settings` parent with `/account` + `/connections` + `/features` children).
 * Chrome only: a header + the tab row; the active tab renders through `children` (the routed `<Outlet/>`). Mirrors
 * the antenna parent+children+Outlet routing block. Renders under RequireBand, so BandContext (capabilities) exists.
 */
export function SettingsScreen({ activeTab, onTab, children }: SettingsScreenProps) {
  // Connections is desktop-primary (spec §3): hide the tab under the mobile shell, which swaps at `lg` — the
  // same threshold `ShellLayout` uses to pick MobileShell (ART-153 pass two).
  const hasTutorials = (useBandTutorials().data?.length ?? 0) > 0;
  const tabs = visibleSettingsTabs(useCapability("band:manage"), { hideConnections: usePhoneLayout(), hasTutorials });

  return (
    // `<Page>` owns the vertical rhythm + the mobile-gated side gutter (the shell supplies none); hand-rolling
    // the rhythm is what the pageShell guard forbids (ART-153 pass two). `title` renders the page's "Settings"
    // h1.
    <Page title="Settings">
      <Tabs tabs={tabs} active={activeTab} onChange={onTab} label="Settings sections" />

      <div className={styles.body}>{children}</div>
    </Page>
  );
}
